import React, { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { WalletDto, WalletType } from 'api/Wallet/Types/Wallet'
import { TransactionTabType } from 'routes/Transactions/Create/TransactionTab'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { hasPermission } from 'utils/permissionsValidator'

interface MenuItemsProps {
  tradingAccount?: TradingAccountDto
  wallet?: WalletDto
  clientData: ClientDetailedDto
  handleAddTradingAccount?: (walletId: string) => void
  updateTradingAccount?: (taId: string) => void
}

interface ClientDetailsWalletMenuItem {
  key: string
  label: React.ReactNode
  onSelect?: () => void
  disabled?: boolean
  children?: ClientDetailsWalletMenuItem[]
}

const renderMenuItems = (items: ClientDetailsWalletMenuItem[]) =>
  items.map((item) =>
    item.children?.length ? (
      <DropdownMenuSub key={item.key}>
        <DropdownMenuSubTrigger disabled={item.disabled}>{item.label}</DropdownMenuSubTrigger>
        <DropdownMenuSubContent>{renderMenuItems(item.children)}</DropdownMenuSubContent>
      </DropdownMenuSub>
    ) : (
      <DropdownMenuItem key={item.key} disabled={item.disabled} onSelect={item.onSelect}>
        {item.label}
      </DropdownMenuItem>
    )
  )

export const ClientDetailsWalletMenuContent: React.FC<MenuItemsProps> = (props) => {
  const { tradingAccount, wallet, handleAddTradingAccount, updateTradingAccount } = props
  const navigate = useNavigate()
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const items: ClientDetailsWalletMenuItem[] = [
    ...(handleAddTradingAccount && wallet
      ? [
          {
            key: 'add_trading_account',
            label: 'Add Trading Account',
            disabled:
              !hasPermission(permissions, PermissionsEnum.CanAddTradingAccount) ||
              wallet.walletType.id === WalletType.IB_FXCFD,
            onSelect: () => handleAddTradingAccount(wallet.id),
          },
        ]
      : []),
    {
      key: 'create_transaction',
      label: 'Create Transaction',
      disabled: !hasPermission(permissions, PermissionsEnum.CanCreateTransactions),
      children: [
        {
          key: 'deposit',
          label: 'Deposit',
          onSelect: () =>
            navigate(
              wallet?.walletType.id === WalletType.ETD
                ? links.createETDTransaction.url
                : links.createTransaction.url,
              {
                state: { wallet, tradingAccount, tab: TransactionTabType.DEPOSIT },
              }
            ),
        },
        {
          key: 'withdrawal',
          label: 'Withdrawal',
          onSelect: () =>
            navigate(
              wallet?.walletType.id === WalletType.ETD
                ? links.createETDTransaction.url
                : links.createTransaction.url,
              {
                state: { wallet, tradingAccount, tab: TransactionTabType.WITHDRAWAL },
              }
            ),
        },
        {
          key: 'transfer',
          label: 'Transfer',
          children: [
            {
              key: 'to_trading_account',
              label: 'To Trading Account',
              onSelect: () =>
                navigate(
                  wallet?.walletType.id === WalletType.ETD
                    ? links.createETDTransaction.url
                    : links.createTransaction.url,
                  {
                    state: {
                      wallet,
                      tradingAccount,
                      tab: TransactionTabType.FROM_WALLET_TO_TRADING_ACCOUNT_TRANSACTION,
                    },
                  }
                ),
            },
            {
              key: 'from_trading_account',
              label: 'From Trading Account',
              onSelect: () =>
                navigate(
                  wallet?.walletType.id === WalletType.ETD
                    ? links.createETDTransaction.url
                    : links.createTransaction.url,
                  {
                    state: {
                      wallet,
                      tradingAccount,
                      tab: TransactionTabType.FROM_TRADING_ACCOUNT_TO_WALLET_TRANSACTION,
                    },
                  }
                ),
            },
            {
              key: 'conversion',
              label: 'Conversion',
              disabled: wallet?.walletType.id !== WalletType.ETD,
              onSelect: () =>
                navigate(links.createETDTransaction.url, {
                  state: {
                    wallet,
                    tradingAccount,
                    tab: TransactionTabType.CONVERSION,
                  },
                }),
            },
          ],
        },
        {
          key: 'bonus',
          label: 'Bonus',
          onSelect: () =>
            navigate(
              wallet?.walletType.id === WalletType.ETD
                ? links.createETDTransaction.url
                : links.createTransaction.url,
              {
                state: { wallet, tradingAccount, tab: TransactionTabType.BONUS },
              }
            ),
        },
        {
          key: 'adjustment',
          label: 'Adjustment',
          onSelect: () =>
            navigate(
              wallet?.walletType.id === WalletType.ETD
                ? links.createETDTransaction.url
                : links.createTransaction.url,
              {
                state: { wallet, tradingAccount, tab: TransactionTabType.ADJUSTMENTS },
              }
            ),
        },
      ],
    },
    ...(updateTradingAccount && tradingAccount?.campaigns.length
      ? [
          {
            key: 'set_automatic_closure_to_false',
            label: 'Automatic Closure',
            disabled: !hasPermission(
              permissions,
              PermissionsEnum.CanEditTradingAccountAutomaticClosure
            ),
            onSelect: () => updateTradingAccount(tradingAccount.id),
          },
        ]
      : []),
  ]

  return <>{renderMenuItems(items)}</>
}
